import { api } from './api';
import { analytics } from 'lib/analytics';
import config from 'data/config';

export const SHARED_POST_CREATE_CACHE_KEY = 'shared-post-create';

const baseUrl = config.masterDataServiceURL;

export const freeTrialApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ------------------------------------------------------------- //
    // Status
    // This is called with a registration id and returns the status
    // ------------------------------------------------------------- //
    status: build.query({
      query: ({ registrationToken }) => {
        return {
          url: `${baseUrl}/api/registration/status`,
          method: 'GET',
          params: { registrationToken },
        };
      },
      transformResponse: async (res) => {
        // Possible Values for data?.result?.status
        // AwaitingVerification
        // EmailConfirmed
        // SiteCreated
        return res;
      },
      providesTags: () => {
        return [{ type: 'login', id: 'FREE_TRIAL_STATUS' }];
      },
    }),
    register: build.query({
      query: ({ body }) => {
        return {
          url: `${baseUrl}/api/registration/register`,
          method: 'POST',
          body,
        };
      },
      transformResponse: async (res, _meta, arg) => {
        const { body } = arg ?? {};
        analytics.freeTrial.trackUser(body);
        analytics.freeTrial.trackRegistration(body);
        return res;
      },
      providesTags: () => {
        return [{ type: 'login', id: 'FREE_TRIAL_REGISTER' }];
      },
    }),
    verify: build.query({
      query: ({ verifyToken }) => {
        return {
          url: `${baseUrl}/api/registration/verify`,
          method: 'POST',
          body: { verifyToken },
        };
      },
      transformResponse: async (res) => {
        const { email, campaignId, companyName } = res;
        analytics.freeTrial.trackUser({ email, campaignId, companyName });
        analytics.freeTrial.trackEmailVerification();
        return res;
      },
      providesTags: () => {
        return [{ type: 'login', id: 'FREE_TRIAL_VERIFY' }];
      },
    }),
    // ------------------------------------------------------------- //
    // MUTATIONS
    // ------------------------------------------------------------- //
    // Registration
    // Sent from the headless landing page on valid data or submit
    // Returns a registration id
    // ------------------------------------------------------------- //
    postRegistration: build.mutation({
      query: ({ body }) => {
        return {
          url: `${baseUrl}/api/registration/register`,
          method: 'POST',
          body,
        };
      },
      transformResponse: async (res, _meta, arg) => {
        const { body } = arg ?? {};
        analytics.freeTrial.trackUser(body);
        analytics.freeTrial.trackRegistration(body);
        return res;
      },
      invalidatesTags: () => [
        { type: 'login', id: 'FREE_TRIAL_STATUS' },
        { type: 'login', id: 'FREE_TRIAL_REGISTER' },
      ],
    }),
    // ------------------------------------------------------------- //
    // Create
    // Starts the process of site creation
    // ------------------------------------------------------------- //
    postCreate: build.mutation({
      query: ({ body }) => {
        return {
          url: `${baseUrl}/api/registration/create`,
          method: 'POST',
          body,
        };
      },
      transformResponse: async (res) => {
        analytics.freeTrial.trackSiteCreation();
        return res;
      },
      invalidatesTags: () => [{ type: 'login', id: 'FREE_TRIAL_STATUS' }],
    }),
    // ------------------------------------------------------------- //
    // Reset
    // Resets the validation email
    // ------------------------------------------------------------- //
    postResend: build.mutation({
      query: ({ body }) => {
        return {
          url: `${baseUrl}/api/registration/resend`,
          method: 'POST',
          body, // Body just has one value: { registrationToken }
        };
      },
      invalidatesTags: () => [
        { type: 'login', id: 'FREE_TRIAL_STATUS' },
        { type: 'login', id: 'FREE_TRIAL_VERIFY' },
        { type: 'login', id: 'FREE_TRIAL_REGISTER' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useStatusQuery,
  useRegisterQuery,
  useVerifyQuery,
  usePostRegistrationMutation,
  usePostCreateMutation,
  usePostResendMutation,
} = freeTrialApi;
